import {ListView} from '@github-ui/list-view'
import {
  actionsWorkflowFilePath,
  actionsWorkflowRunPath,
  branchPath,
  commitHovercardPath,
  repoAttestationDownloadPath,
  repoAttestationsIndexPath,
} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ArrowLeftIcon, DownloadIcon, PackageIcon, VerifiedIcon} from '@primer/octicons-react'
import {Button, Heading, Link, PageLayout, RelativeTime} from '@primer/react'
import {DataTable, Table} from '@primer/react/experimental'

import {AttestationPropertiesList} from '../components/AttestationPropertiesList'
import {VerifyAttestation} from '../components/VerifyAttestation'
import type {AttestationPayload} from '../types'
import styles from './AttestationDetail.module.css'

export const AttestationDetail = () => {
  // attestation payload
  const payload = useRoutePayload<AttestationPayload>()

  // run id and attempt
  const [_, runId, attempt] =
    payload.attestation.runInvocationUri.match(/\/actions\/runs\/(\d+)\/attempts\/(\d+)$/) || []

  // structure the data for certificate summary
  const certificateData = [
    {
      label: 'Build Config Digest',
      value: payload.attestation.buildConfigDigest,
    },
    {
      label: 'Build Config URI',
      value: payload.attestation.buildConfigUri,
    },
    {
      label: 'Build Signer Digest',
      value: payload.attestation.buildSignerDigest,
    },
    {
      label: 'Build Signer URI',
      value: payload.attestation.buildSignerUri,
    },
    {
      label: 'Build Trigger',
      value: payload.attestation.buildTrigger,
    },
    {
      label: 'Issuer',
      value: payload.attestation.issuer,
    },
    {
      label: 'Runner Invocation URI',
      value: payload.attestation.runInvocationUri,
    },
    {
      label: 'Runner Environment',
      value: payload.attestation.runnerEnvironment,
    },
    {
      label: 'Source Repository Digest',
      value: payload.attestation.sourceRepositoryDigest,
    },
    {
      label: 'Source Repository Identifier',
      value: payload.attestation.sourceRepositoryIdentifier,
    },
    {
      label: 'Source Repository Owner Identifier',
      value: payload.attestation.sourceRepositoryOwnerIdentifier,
    },
    {
      label: 'Source Repository Owner URI',
      value: payload.attestation.sourceRepositoryOwnerUri,
    },
    {
      label: 'Source Repository Ref',
      value: payload.attestation.sourceRepositoryRef,
    },
    {
      label: 'Source Repository URI',
      value: payload.attestation.sourceRepositoryUri,
    },
    {
      label: 'Source Repository Visibility',
      value: payload.attestation.sourceRepositoryVisibilityAtSigning,
    },
  ]

  // Add an id to each item in the certificate data for DataTable
  const certificateDataWithId = certificateData.map((item, index) => ({
    ...item,
    id: index.toString(),
  }))

  const verifyCommand =
    // gh attestation verify command must include predicate-type flag if it's a non-provenance attestation
    payload.attestation.predicateType === 'https://slsa.dev/provenance/v1'
      ? `gh attestation verify --owner ${payload.repo.ownerLogin} <filename-or-url>`
      : `gh attestation verify --owner ${payload.repo.ownerLogin} --predicate-type "${payload.attestation.predicateType}" <filename-or-url>`

  return (
    <PageLayout>
      <PageLayout.Header>
        <div>
          <Link
            href={repoAttestationsIndexPath({
              repo: payload.repo,
            })}
            className={styles.Link}
          >
            <ArrowLeftIcon size={16} /> Attestations
          </Link>
        </div>
        <div className={styles.Box}>
          <Heading as="h2" className={styles.Heading}>
            <div className={styles.Box_1}>
              <VerifiedIcon size={24} />
            </div>
            <span className={styles.PredicateType}>{payload.attestation.predicateType}</span>
            <span className={styles.Text}>{`#${payload.attestation.id}`}</span>
          </Heading>
          <div className={styles.Box_2}>
            <Button
              as="a"
              data-turbo="false"
              data-testid="download-attestation-detail"
              href={repoAttestationDownloadPath({
                repo: payload.repo,
                attestationId: payload.attestation.id,
              })}
            >
              <DownloadIcon size={16} />
              <span className={styles.Text_1}>Download</span>
            </Button>
          </div>
        </div>
      </PageLayout.Header>
      <PageLayout.Content as="div">
        <ListView variant="default" title="Title of the list" titleHeaderTag="h3" />
        <AttestationPropertiesList
          definitions={[
            {
              propertyName: 'Created',
              propertyValue: (
                <div className={styles.Box_3}>
                  <RelativeTime
                    data-testid="attestation-relative-time"
                    tense="past"
                    datetime={payload.attestation.createdAt}
                  >
                    {new Date(Date.parse(payload.attestation.createdAt)).toLocaleDateString('default', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </RelativeTime>{' '}
                  ({new Date(Date.parse(payload.attestation.createdAt)).toUTCString()})
                </div>
              ),
            },
            {
              propertyName: 'Commit',
              propertyValue: (
                <div className={styles.Box_4}>
                  <Link
                    data-testid="attestation-commit-link"
                    href={branchPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      branch: payload.attestation.sourceRepositoryDigest,
                    })}
                    data-hovercard-url={commitHovercardPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      commitish: payload.attestation.sourceRepositoryDigest,
                    })}
                    className={styles.Link_1}
                  >
                    {payload.attestation.sourceRepositoryDigest}
                  </Link>
                </div>
              ),
            },
            {
              propertyName: 'Build Summary',
              propertyValue: (
                <div className={styles.Box_4}>
                  <Link
                    data-testid="view-build-summary"
                    href={payload.attestation.runInvocationUri}
                    inline
                    className={styles.Link_2}
                  >
                    {actionsWorkflowRunPath({
                      repo: payload.repo.name,
                      owner: payload.repo.ownerLogin,
                      runId,
                      attempt,
                    })}
                  </Link>
                </div>
              ),
            },
            {
              propertyName: 'Workflow File',
              propertyValue: (
                <div className={styles.Box_4}>
                  <Link
                    data-testid="attestation-workflow-link"
                    href={actionsWorkflowFilePath({
                      repo: payload.repo.name,
                      owner: payload.repo.ownerLogin,
                      runId,
                    })}
                    inline
                    className={styles.Link_1}
                  >
                    {payload.attestation.buildConfigDisplayName}@{payload.attestation.sourceRepositoryRef}
                  </Link>
                </div>
              ),
            },
          ]}
        />

        <div className={styles.Box_5}>
          <Heading as="h2" className={styles.Heading_1}>
            Verify
          </Heading>
          <VerifyAttestation url={verifyCommand} />
        </div>

        <div className={styles.Box_5}>
          <Table.Container>
            <Table.Title id="certificate-summary">Certificate Summary</Table.Title>
            <DataTable
              cellPadding="normal"
              data={certificateDataWithId}
              columns={[
                {
                  field: 'label',
                  header: 'Key',
                  rowHeader: true,
                  width: '35%',
                },
                {
                  field: 'value',
                  header: 'Value',
                  rowHeader: false,
                  width: '65%',
                },
              ]}
            />
          </Table.Container>
        </div>

        <div className={styles.Box_5}>
          <Table.Container>
            <Table.Title id="subjects">Subjects</Table.Title>
            <DataTable
              cellPadding="normal"
              aria-labelledby="subjects"
              aria-describedby="subjects-subtitle"
              data={payload.attestation.subjects.map((subject, index) => ({...subject, id: index}))}
              columns={[
                {
                  header: 'Subject',
                  width: '35%',
                  field: 'subjectName',
                  rowHeader: false,
                  renderCell: row => {
                    return (
                      <div>
                        <PackageIcon size={16} className={'mr-2'} verticalAlign={'middle'} />
                        {row.subjectName || '[unnamed subject]'}
                      </div>
                    )
                  },
                },
                {
                  header: 'Subject digest',
                  field: 'subjectDigest',
                  rowHeader: false,
                  width: '65%',
                  renderCell: row => {
                    return <span className={`text-mono ${styles.SubjectDigest}`}>{row.subjectDigest}</span>
                  },
                },
              ]}
            />
          </Table.Container>
        </div>
      </PageLayout.Content>
    </PageLayout>
  )
}
